import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast, Toaster } from "sonner";
import { motion } from "framer-motion";
import { LogOut, Trash2, Mail, Phone, RefreshCw, Inbox } from "lucide-react";
import { Logo } from "../components/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "ma_admin_token";
const STATUSES = ["new", "contacted", "confirmed", "closed"];
const STATUS_STYLES = {
  new: "bg-[#D4AF37]/15 text-[#D4AF37] border-[#D4AF37]/40",
  contacted: "bg-sky-400/10 text-sky-300 border-sky-400/30",
  confirmed: "bg-emerald-400/10 text-emerald-300 border-emerald-400/30",
  closed: "bg-slate-500/10 text-slate-400 border-slate-500/30",
};

const formatError = (detail) => {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return detail?.msg || String(detail);
};

export const Admin = () => {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [checking, setChecking] = useState(!!token);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loginError, setLoginError] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);
  const [enquiries, setEnquiries] = useState(null);
  const [filter, setFilter] = useState("all");

  const authHeaders = useCallback(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/enquiries`, { headers: authHeaders() });
      setEnquiries(data.enquiries);
    } catch (e) {
      if (e.response?.status === 401) {
        localStorage.removeItem(TOKEN_KEY);
        setToken("");
      } else {
        toast.error("Failed to load enquiries");
      }
    }
  }, [authHeaders]);

  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        await axios.get(`${API}/auth/me`, { headers: authHeaders() });
        await load();
      } catch {
        localStorage.removeItem(TOKEN_KEY);
        setToken("");
      } finally {
        setChecking(false);
      }
    })();
  }, [token, authHeaders, load]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoggingIn(true);
    setLoginError("");
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
      setChecking(true);
    } catch (err) {
      setLoginError(formatError(err.response?.data?.detail));
    } finally {
      setLoggingIn(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setEnquiries(null);
  };

  const setStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/enquiries/${id}`, { status }, { headers: authHeaders() });
      setEnquiries((list) => list.map((q) => (q.id === id ? { ...q, status } : q)));
      toast.success(`Marked as ${status}`);
    } catch {
      toast.error("Failed to update status");
    }
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/enquiries/${id}`, { headers: authHeaders() });
      setEnquiries((list) => list.filter((q) => q.id !== id));
      toast.success("Enquiry deleted");
    } catch {
      toast.error("Failed to delete enquiry");
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-[#050B14] flex items-center justify-center px-6 grain-overlay" data-testid="admin-login-page">
        <motion.form
          onSubmit={handleLogin}
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="w-full max-w-md border border-[rgba(212,175,55,0.25)] bg-[#0B1528]/80 backdrop-blur-xl p-10"
          data-testid="admin-login-form"
        >
          <div className="flex justify-center mb-8"><Logo compact /></div>
          <p className="font-label text-[10px] tracking-[0.4em] uppercase text-[#D4AF37] text-center mb-8">Admin — Enquiries</p>
          <label className="font-label text-[10px] tracking-[0.3em] uppercase text-slate-500 block mb-1">Email</label>
          <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="lux-input mb-6" data-testid="admin-login-email-input" />
          <label className="font-label text-[10px] tracking-[0.3em] uppercase text-slate-500 block mb-1">Password</label>
          <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="lux-input mb-2" data-testid="admin-login-password-input" />
          {loginError && <p className="text-sm text-red-400 mt-3" data-testid="admin-login-error">{loginError}</p>}
          <button
            type="submit"
            disabled={loggingIn}
            data-testid="admin-login-submit-button"
            className="w-full mt-8 font-label text-[11px] tracking-[0.3em] uppercase py-4 bg-[#D4AF37] text-[#050B14] font-semibold hover:bg-[#F3E5AB] disabled:opacity-50 transition-colors duration-300"
          >
            {loggingIn ? "Signing in…" : "Sign in"}
          </button>
          <a href="/" className="block text-center mt-6 font-label text-[10px] tracking-[0.25em] uppercase text-slate-500 hover:text-[#D4AF37] transition-colors" data-testid="admin-back-to-site-link">
            ← Back to website
          </a>
        </motion.form>
        <Toaster position="bottom-center" toastOptions={{ style: { background: "#0B1528", border: "1px solid rgba(212,175,55,0.35)", color: "#F7F5F0" } }} />
      </div>
    );
  }

  const filtered = enquiries ? (filter === "all" ? enquiries : enquiries.filter((q) => q.status === filter)) : [];
  const newCount = enquiries ? enquiries.filter((q) => q.status === "new").length : 0;

  return (
    <div className="min-h-screen bg-[#050B14] grain-overlay" data-testid="admin-dashboard">
      <header className="border-b border-[rgba(212,175,55,0.15)] bg-[#0B1528]/70 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-6 h-20 flex items-center justify-between">
          <Logo compact />
          <div className="flex items-center gap-3">
            <button onClick={load} aria-label="Refresh" data-testid="admin-refresh-button" className="w-10 h-10 flex items-center justify-center border border-[rgba(212,175,55,0.25)] text-[#D4AF37] hover:bg-[#D4AF37]/10 transition-colors">
              <RefreshCw size={15} />
            </button>
            <button onClick={handleLogout} data-testid="admin-logout-button" className="flex items-center gap-2 font-label text-[10px] tracking-[0.25em] uppercase px-5 py-2.5 border border-[rgba(212,175,55,0.25)] text-slate-300 hover:text-[#D4AF37] hover:border-[#D4AF37] transition-colors">
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-12">
        {checking || enquiries === null ? (
          <p className="text-slate-400 font-light" data-testid="admin-loading">Loading enquiries…</p>
        ) : (
          <>
            <div className="flex flex-wrap items-end justify-between gap-6 mb-10">
              <div>
                <p className="font-label text-[10px] tracking-[0.4em] uppercase text-[#D4AF37] mb-3">Charter Enquiries</p>
                <h1 className="font-display text-4xl sm:text-5xl font-light text-[#F7F5F0]" data-testid="admin-title">
                  {enquiries.length} {enquiries.length === 1 ? "Enquiry" : "Enquiries"}
                </h1>
                <p className="text-sm text-slate-400 mt-2 font-light" data-testid="admin-new-count">{newCount} new awaiting reply</p>
              </div>
              <div className="flex flex-wrap gap-2" data-testid="admin-filters">
                {["all", ...STATUSES].map((s) => (
                  <button
                    key={s}
                    onClick={() => setFilter(s)}
                    data-testid={`admin-filter-${s}`}
                    className={`font-label text-[10px] tracking-[0.2em] uppercase px-4 py-2 border transition-colors duration-300 ${
                      filter === s ? "border-[#D4AF37] text-[#D4AF37] bg-[#D4AF37]/10" : "border-[rgba(212,175,55,0.2)] text-slate-400 hover:text-[#F3E5AB]"
                    }`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>

            {filtered.length === 0 ? (
              <div className="border border-[rgba(212,175,55,0.15)] p-16 text-center" data-testid="admin-empty-state">
                <Inbox size={32} className="text-[#D4AF37]/50 mx-auto mb-4" strokeWidth={1.5} />
                <p className="text-slate-400 font-light">No enquiries in this view yet.</p>
              </div>
            ) : (
              <div className="space-y-5">
                {filtered.map((q) => (
                  <motion.article
                    key={q.id}
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                    className="border border-[rgba(212,175,55,0.15)] bg-[#0B1528]/60 p-6 sm:p-8"
                    data-testid={`enquiry-card-${q.id}`}
                  >
                    <div className="flex flex-wrap items-start justify-between gap-4 mb-5">
                      <div>
                        <div className="flex flex-wrap items-center gap-3">
                          <h3 className="font-display text-2xl text-[#F7F5F0] font-light">{q.name}</h3>
                          <span className={`font-label text-[9px] tracking-[0.25em] uppercase border px-3 py-1 ${STATUS_STYLES[q.status] || STATUS_STYLES.new}`} data-testid={`enquiry-status-${q.id}`}>
                            {q.status}
                          </span>
                        </div>
                        <p className="font-label text-[10px] tracking-[0.2em] uppercase text-slate-500 mt-2">
                          {new Date(q.created_at).toLocaleString()} {q.language ? `· ${q.language.toUpperCase()}` : ""}
                        </p>
                      </div>
                      <button onClick={() => remove(q.id)} aria-label="Delete enquiry" data-testid={`enquiry-delete-${q.id}`} className="w-9 h-9 flex items-center justify-center border border-red-400/30 text-red-400/80 hover:bg-red-400/10 transition-colors">
                        <Trash2 size={15} />
                      </button>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-3 mb-5 text-sm">
                      <a href={`mailto:${q.email}`} className="flex items-center gap-2.5 text-slate-300 hover:text-[#D4AF37] transition-colors" data-testid={`enquiry-email-${q.id}`}>
                        <Mail size={14} className="text-[#D4AF37]" /> {q.email}
                      </a>
                      {q.phone && (
                        <a href={`tel:${q.phone}`} className="flex items-center gap-2.5 text-slate-300 hover:text-[#D4AF37] transition-colors" data-testid={`enquiry-phone-${q.id}`}>
                          <Phone size={14} className="text-[#D4AF37]" /> {q.phone}
                        </a>
                      )}
                      {q.vessel && <p className="text-slate-400 font-light">Vessel: <span className="text-[#F7F5F0]">{q.vessel}</span></p>}
                      {q.destination && <p className="text-slate-400 font-light">Destination: <span className="text-[#F7F5F0]">{q.destination}</span></p>}
                      {q.dates && <p className="text-slate-400 font-light">Dates: <span className="text-[#F7F5F0]">{q.dates}</span></p>}
                    </div>

                    <p className="text-sm text-slate-300 font-light leading-relaxed border-l-2 border-[#D4AF37]/40 pl-4 mb-6">{q.message}</p>

                    <div className="flex flex-wrap gap-2">
                      {STATUSES.map((s) => (
                        <button
                          key={s}
                          onClick={() => setStatus(q.id, s)}
                          disabled={q.status === s}
                          data-testid={`enquiry-set-${s}-${q.id}`}
                          className={`font-label text-[9px] tracking-[0.2em] uppercase px-3.5 py-2 border transition-colors duration-300 disabled:opacity-30 ${
                            q.status === s ? "border-[#D4AF37] text-[#D4AF37]" : "border-[rgba(212,175,55,0.2)] text-slate-400 hover:text-[#F3E5AB] hover:border-[#D4AF37]/50"
                          }`}
                        >
                          {s}
                        </button>
                      ))}
                    </div>
                  </motion.article>
                ))}
              </div>
            )}
          </>
        )}
      </main>
      <Toaster position="bottom-center" toastOptions={{ style: { background: "#0B1528", border: "1px solid rgba(212,175,55,0.35)", color: "#F7F5F0" } }} />
    </div>
  );
};
